import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Target, 
  TrendingUp, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Clock,
  AlertCircle,
  X,
  Save,
  Calendar,
  User,
  Scale,
  Award,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { performanceApi, employeeApi } from '../../services/api';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

interface KPI {
  id: string;
  metric: string;
  target: string;
  progress: number;
  unit?: string;
}

interface Goal {
  id: string;
  employee_id: string;
  title: string;
  description?: string;
  target_date: string;
  year: number;
  status: string;
  progress: number;
  weight?: number;
  kpi_metrics?: KPI[];
  created_at: string;
  updated_at: string;
}

const GoalsManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  const [isKPIDialogOpen, setIsKPIDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [filterEmployee, setFilterEmployee] = useState<string>('all');
  const [filterYear, setFilterYear] = useState<number>(new Date().getFullYear());
  
  const [goalFormData, setGoalFormData] = useState({
    employee_id: '',
    title: '',
    description: '',
    target_date: '',
    year: new Date().getFullYear(),
    status: 'NOT_STARTED',
    progress: 0,
    weight: 1,
  });

  const [kpiFormData, setKpiFormData] = useState({
    metric: '',
    target: '',
    progress: 0,
    unit: '',
  });

  // Fetch goals
  const { data: goals = [], isLoading: goalsLoading } = useQuery({
    queryKey: ['performanceGoals', filterEmployee, filterYear],
    queryFn: () => performanceApi.goals.getAll({
      employee_id: filterEmployee !== 'all' ? filterEmployee : undefined,
      year: filterYear,
    }),
  });

  // Fetch employees
  const { data: employeesData = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });
  
  const employees = Array.isArray(employeesData) ? employeesData : [];

  // Create goal mutation
  const createGoalMutation = useMutation({
    mutationFn: performanceApi.goals.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      setIsGoalDialogOpen(false);
      resetGoalForm();
      toast({ title: 'Goal created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Update goal mutation
  const updateGoalMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => performanceApi.goals.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      setIsGoalDialogOpen(false);
      setEditingGoal(null);
      resetGoalForm();
      toast({ title: 'Goal updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete goal mutation
  const deleteGoalMutation = useMutation({
    mutationFn: (id: string) => performanceApi.goals.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      toast({ title: 'Goal deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Add KPI mutation
  const addKPIMutation = useMutation({
    mutationFn: ({ goalId, kpi }: { goalId: string; kpi: any }) => 
      performanceApi.goals.addKPI(goalId, kpi),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      setIsKPIDialogOpen(false);
      resetKPIForm();
      toast({ title: 'KPI added successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Remove KPI mutation
  const removeKPIMutation = useMutation({
    mutationFn: ({ goalId, kpiId }: { goalId: string; kpiId: string }) => 
      performanceApi.goals.removeKPI(goalId, kpiId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      toast({ title: 'KPI removed successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Update KPI progress mutation
  const updateKPIProgressMutation = useMutation({
    mutationFn: ({ goalId, kpiId, progress }: { goalId: string; kpiId: string; progress: number }) => 
      performanceApi.goals.updateKPIProgress(goalId, kpiId, progress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });
      toast({ title: 'KPI progress updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const resetGoalForm = () => {
    setGoalFormData({
      employee_id: '',
      title: '',
      description: '',
      target_date: '',
      year: new Date().getFullYear(),
      status: 'NOT_STARTED',
      progress: 0,
      weight: 1,
    });
    setEditingGoal(null);
  };

  const resetKPIForm = () => {
    setKpiFormData({
      metric: '',
      target: '',
      progress: 0,
      unit: '',
    });
  };

  const handleGoalSubmit = () => {
    if (!goalFormData.employee_id || !goalFormData.title || !goalFormData.target_date) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    if (editingGoal) {
      updateGoalMutation.mutate({ id: editingGoal.id, data: goalFormData });
    } else {
      createGoalMutation.mutate(goalFormData);
    }
  };

  const handleKPISubmit = () => {
    if (!selectedGoal || !kpiFormData.metric || !kpiFormData.target) {
      toast({ title: 'Error', description: 'Please fill all required KPI fields', variant: 'destructive' });
      return;
    }

    const kpiData = {
      id: `kpi_${Date.now()}`,
      ...kpiFormData,
    };

    addKPIMutation.mutate({ goalId: selectedGoal.id, kpi: kpiData });
  };

  const handleDeleteGoal = (goal: Goal) => {
    if (window.confirm(`Are you sure you want to delete the goal "${goal.title}"?`)) {
      deleteGoalMutation.mutate(goal.id);
    }
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setGoalFormData({
      employee_id: goal.employee_id,
      title: goal.title,
      description: goal.description || '',
      target_date: goal.target_date,
      year: goal.year,
      status: goal.status,
      progress: goal.progress,
      weight: goal.weight || 1,
    });
    setIsGoalDialogOpen(true);
  };

  const handleAddKPI = (goal: Goal) => {
    setSelectedGoal(goal);
    resetKPIForm();
    setIsKPIDialogOpen(true);
  };

  const handleRemoveKPI = (goal: Goal, kpiId: string) => {
    if (window.confirm('Are you sure you want to remove this KPI?')) {
      removeKPIMutation.mutate({ goalId: goal.id, kpiId });
    }
  };

  const handleUpdateKPIProgress = (goal: Goal, kpiId: string, progress: number) => {
    updateKPIProgressMutation.mutate({ goalId: goal.id, kpiId, progress });
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      NOT_STARTED: 'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700/50',
      IN_PROGRESS: 'bg-blue-50 text-blue-700 border-blue-200/50 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/50',
      COMPLETED:   'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50',
      CANCELLED:   'bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800/50',
    };
    const key = status?.toUpperCase() || 'NOT_STARTED';
    const classNames = statusColors[key] || 'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700/50';
    const label = status.replace('_', ' ');
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[11px] tracking-wide shadow-none border shrink-0", classNames)} variant="outline">
        {label}
      </Badge>
    );
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find((e: any) => e.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';
  };

  const getEmployeeInitials = (name: string) => {
    const parts = name.trim().split(' ');
    const first = parts[0]?.charAt(0) || '';
    const last = parts[parts.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  // Calculate statistics
  const goalsArray = Array.isArray(goals) ? goals : [];
  const totalGoals = goalsArray.length;
  const completedGoals = goalsArray.filter((g: Goal) => g.status === 'COMPLETED').length;
  const inProgressGoals = goalsArray.filter((g: Goal) => g.status === 'IN_PROGRESS').length;
  const avgProgress = goalsArray.length > 0 
    ? Math.round(goalsArray.reduce((sum: number, g: Goal) => sum + g.progress, 0) / goalsArray.length)
    : 0;

  const years = Array.from(new Set(goalsArray.map((g: Goal) => g.year))).sort((a, b) => b - a);
  if (years.length === 0) years.push(new Date().getFullYear());

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header section with inline controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Set and track performance goals with KPIs</h2>
          
        </div>
        
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          <Button 
            onClick={() => { resetGoalForm(); setIsGoalDialogOpen(true); }}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Goal
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Total Goals</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{totalGoals}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Assigned Goals</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-[#143A62] dark:text-blue-400 shadow-sm">
                <Target className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">In Progress</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{inProgressGoals}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Underway Goals</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-900/20 text-amber-500 shadow-sm">
                <Clock className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Completed</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{completedGoals}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Finished Goals</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 shadow-sm">
                <CheckCircle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-400 tracking-wide">Avg Progress</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{avgProgress}%</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Overall Progress Rate</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-purple-50 dark:bg-purple-900/20 text-purple-500 shadow-sm">
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm border border-slate-200 dark:border-slate-800 p-4 rounded-2xl shadow-sm grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-bold text-slate-400 tracking-wider">Employee</Label>
          <Select value={filterEmployee} onValueChange={setFilterEmployee}>
            <SelectTrigger className="border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-10 rounded-xl shadow-sm">
              <SelectValue placeholder="Filter by employee" />
            </SelectTrigger>
            <SelectContent className="border-slate-100 dark:border-slate-800">
              <SelectItem value="all" className="text-xs font-bold">All Employees</SelectItem>
              {employees.map((emp: any) => (
                <SelectItem key={emp.id} value={emp.id} className="text-xs font-bold">
                  {emp.first_name} {emp.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold text-slate-400 tracking-wider">Year</Label>
          <Select value={filterYear.toString()} onValueChange={(val) => setFilterYear(parseInt(val))}>
            <SelectTrigger className="border-slate-200 dark:border-slate-850 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-10 rounded-xl shadow-sm">
              <SelectValue placeholder="Filter by year" />
            </SelectTrigger>
            <SelectContent className="border-slate-100 dark:border-slate-800">
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()} className="text-xs font-bold">
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Goals List */}
      <div className="space-y-6">
        {goalsLoading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 font-bold text-slate-400">Loading goals...</span>
          </div>
        ) : goalsArray.length === 0 ? (
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl p-16 text-center">
            <div className="bg-slate-50 dark:bg-slate-800 h-16 w-16 rounded-2xl flex items-center justify-center mb-4 border border-slate-100 dark:border-slate-700 mx-auto">
              <Target className="h-8 w-8 text-slate-350 dark:text-slate-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">No goals found</h3>
            <p className="text-sm text-slate-400 mt-1">Create your first goal to get started!</p>
          </Card>
        ) : (
          goalsArray.map((goal: Goal) => {
            const empName = getEmployeeName(goal.employee_id);
            return (
              <Card key={goal.id} className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl group hover:translate-y-[-1px] transition-all duration-300">
                <CardHeader className="pb-4 pt-6 px-8 border-b border-slate-50 dark:border-slate-850/50">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">{goal.title}</CardTitle>
                        {getStatusBadge(goal.status)}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs font-bold text-slate-400 mt-1.5">
                        <div className="flex items-center gap-1.5">
                          <div className="h-5 w-5 rounded-md bg-gradient-to-br from-[#143A62]/10 to-blue-500/10 text-[#143A62] dark:text-blue-400 font-bold text-[9px] flex items-center justify-center border border-slate-100/50 shrink-0">
                            {getEmployeeInitials(empName)}
                          </div>
                          <span>Employee: {empName}</span>
                        </div>
                        <span className="text-slate-200 dark:text-slate-800">•</span>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 text-blue-500" />
                          <span>Target: {format(new Date(goal.target_date), 'MMM dd, yyyy')}</span>
                        </div>
                        <span className="text-slate-200 dark:text-slate-800">•</span>
                        <div className="flex items-center gap-1">
                          <TrendingUp className="h-3.5 w-3.5 text-purple-500" />
                          <span>Progress: {goal.progress}%</span>
                        </div>
                        {goal.weight !== undefined && (
                          <>
                            <span className="text-slate-200 dark:text-slate-800">•</span>
                            <div className="flex items-center gap-1">
                              <Scale className="h-3.5 w-3.5 text-slate-400" />
                              <span>Weight: {goal.weight}x</span>
                            </div>
                          </>
                        )}
                      </div>
                      
                      {goal.description && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-3 italic bg-slate-50/50 dark:bg-slate-900/50 p-3 rounded-xl border border-slate-100 dark:border-slate-850/50 leading-relaxed font-semibold">
                          {goal.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleEditGoal(goal)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleDeleteGoal(goal)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-8 space-y-6">
                  {/* Overall Progress */}
                  <div>
                    <div className="flex justify-between text-xs font-bold mb-2">
                      <span className="text-slate-700 dark:text-slate-350">Overall Progress</span>
                      <span className="text-slate-800 dark:text-white font-black bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-md leading-none">{goal.progress}%</span>
                    </div>
                    <Progress value={goal.progress} className="h-2 shadow-sm" />
                  </div>

                  {/* KPIs */}
                  <div className="border-t border-slate-100 dark:border-slate-800/80 pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-sm font-bold text-[#143A62] dark:text-slate-200">Key Performance Indicators</h4>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleAddKPI(goal)}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Add KPI
                      </Button>
                    </div>

                    {goal.kpi_metrics && goal.kpi_metrics.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {goal.kpi_metrics.map((kpi: KPI) => (
                          <KPICard
                            key={kpi.id}
                            kpi={kpi}
                            onUpdateProgress={(progress) => handleUpdateKPIProgress(goal, kpi.id, progress)}
                            onRemove={() => handleRemoveKPI(goal, kpi.id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 border border-dashed rounded-2xl bg-slate-50/30 dark:bg-slate-900/10">
                        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-300 dark:text-slate-700" />
                        <p className="text-xs font-bold text-slate-400">No KPIs defined yet</p>
                        <Button 
                          size="sm" 
                          variant="link" 
                          onClick={() => handleAddKPI(goal)}
                        >
                          Add your first KPI
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Goal Dialog */}
      <Dialog open={isGoalDialogOpen} onOpenChange={setIsGoalDialogOpen}>
        <DialogContent className="w-[min(96vw,600px)] max-w-[600px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 transition-colors duration-300 gap-0 overflow-hidden shadow-2xl">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-500" />
              {editingGoal ? 'Edit Goal' : 'Create New Goal'}
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Set goal parameters and target boundaries
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="employee" className="text-xs font-bold text-slate-400 tracking-wider">Employee *</Label>
              <Select 
                value={goalFormData.employee_id} 
                onValueChange={(val) => setGoalFormData({ ...goalFormData, employee_id: val })}
              >
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent className="border-slate-100 dark:border-slate-800">
                  {employees.map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id} className="text-sm font-bold">
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="title" className="text-xs font-bold text-slate-400 tracking-wider">Goal Title *</Label>
              <Input
                id="title"
                value={goalFormData.title}
                onChange={(e) => setGoalFormData({ ...goalFormData, title: e.target.value })}
                placeholder="e.g., Increase sales by 25%"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-xs font-bold text-slate-400 tracking-wider">Description</Label>
              <Textarea
                id="description"
                value={goalFormData.description}
                onChange={(e) => setGoalFormData({ ...goalFormData, description: e.target.value })}
                placeholder="Describe the goal in detail..."
                rows={3}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="target_date" className="text-xs font-bold text-slate-400 tracking-wider">Target Date *</Label>
                <Input
                  id="target_date"
                  type="date"
                  value={goalFormData.target_date}
                  onChange={(e) => setGoalFormData({ ...goalFormData, target_date: e.target.value })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="year" className="text-xs font-bold text-slate-400 tracking-wider">Year</Label>
                <Input
                  id="year"
                  type="number"
                  value={goalFormData.year}
                  onChange={(e) => setGoalFormData({ ...goalFormData, year: parseInt(e.target.value) || new Date().getFullYear() })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="status" className="text-xs font-bold text-slate-400 tracking-wider">Status</Label>
                <Select 
                  value={goalFormData.status} 
                  onValueChange={(val) => setGoalFormData({ ...goalFormData, status: val })}
                >
                  <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-100 dark:border-slate-800">
                    <SelectItem value="NOT_STARTED" className="text-xs font-bold">Not Started</SelectItem>
                    <SelectItem value="IN_PROGRESS" className="text-xs font-bold">In Progress</SelectItem>
                    <SelectItem value="COMPLETED" className="text-xs font-bold">Completed</SelectItem>
                    <SelectItem value="CANCELLED" className="text-xs font-bold">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="weight" className="text-xs font-bold text-slate-400 tracking-wider">Weight</Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  value={goalFormData.weight}
                  onChange={(e) => setGoalFormData({ ...goalFormData, weight: parseFloat(e.target.value) || 1 })}
                  className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsGoalDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGoalSubmit}>
              {editingGoal ? 'Update Goal' : 'Create Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KPI Dialog */}
      <Dialog open={isKPIDialogOpen} onOpenChange={setIsKPIDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-500" />
              Add KPI Metric
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Establish targeted metric limits for "{selectedGoal?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="p-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="metric" className="text-xs font-bold text-slate-400 tracking-wider">Metric Name *</Label>
              <Input
                id="metric"
                value={kpiFormData.metric}
                onChange={(e) => setKpiFormData({ ...kpiFormData, metric: e.target.value })}
                placeholder="e.g., Customer Satisfaction Score"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-purple-500/20 font-bold text-sm h-11"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="target" className="text-xs font-bold text-slate-400 tracking-wider">Target *</Label>
              <Input
                id="target"
                value={kpiFormData.target}
                onChange={(e) => setKpiFormData({ ...kpiFormData, target: e.target.value })}
                placeholder="e.g., 90% or 1000 units"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-purple-500/20 font-bold text-sm h-11"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="unit" className="text-xs font-bold text-slate-400 tracking-wider">Unit (Optional)</Label>
              <Input
                id="unit"
                value={kpiFormData.unit}
                onChange={(e) => setKpiFormData({ ...kpiFormData, unit: e.target.value })}
                placeholder="e.g., %, units, hours"
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-purple-500/20 font-bold text-sm h-11"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="progress" className="text-xs font-bold text-slate-400 tracking-wider">Initial Progress (%)</Label>
              <Input
                id="progress"
                type="number"
                min="0"
                max="100"
                value={kpiFormData.progress}
                onChange={(e) => setKpiFormData({ ...kpiFormData, progress: parseInt(e.target.value) || 0 })}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-purple-500/20 font-bold text-sm h-11"
              />
            </div>
          </div>
          
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsKPIDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleKPISubmit}>
              Add KPI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// KPI Card Component
interface KPICardProps {
  kpi: KPI;
  onUpdateProgress: (progress: number) => void;
  onRemove: () => void;
}

const KPICard: React.FC<KPICardProps> = ({ kpi, onUpdateProgress, onRemove }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [progress, setProgress] = useState(kpi.progress);

  const handleSave = () => {
    onUpdateProgress(progress);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setProgress(kpi.progress);
    setIsEditing(false);
  };

  return (
    <div className="bg-slate-50/50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800/80 rounded-xl p-4 space-y-3 shadow-none hover:translate-y-[-1px] transition-all duration-300">
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1">
          <h5 className="font-bold text-sm text-slate-800 dark:text-slate-200">{kpi.metric}</h5>
          <p className="text-xs font-bold text-slate-400 mt-1">
            Target: <span className="text-slate-600 dark:text-slate-350">{kpi.target} {kpi.unit && `(${kpi.unit})`}</span>
          </p>
        </div>
        <div className="flex gap-1.5">
          {!isEditing && (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setIsEditing(true)}
            >
              <Edit className="h-3 w-3" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onRemove}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-3 pt-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="number"
              min="0"
              max="100"
              value={progress}
              onChange={(e) => setProgress(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
              className="w-20 rounded-lg h-8 font-bold text-xs"
            />
            <span className="text-xs font-bold text-slate-400">%</span>
            <Button 
              size="sm" 
              onClick={handleSave}
              className="bg-emerald-600 hover:bg-emerald-700 text-white dark:bg-emerald-600 dark:hover:bg-emerald-700 font-bold"
            >
              <Save className="h-3.5 w-3.5 mr-1" />
              Save
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={handleCancel}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Cancel
            </Button>
          </div>
          <Progress value={progress} className="h-1.5 shadow-sm" />
        </div>
      ) : (
        <div className="space-y-1.5 pt-1">
          <div className="flex justify-between text-[11px] font-bold">
            <span className="text-slate-400">Progress</span>
            <span className="text-[#143A62] dark:text-blue-400">{kpi.progress}%</span>
          </div>
          <Progress value={kpi.progress} className="h-1.5 shadow-sm" />
        </div>
      )}
    </div>
  );
};

export default GoalsManagement;
